import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { FORECAST_DESCRIPTION, FORECAST_SLUG_TITLE } from '../consts/seo-copy';
import 'intersection-observer';
import config from '../config';
import Collection from '../components/Collections/Collection';
import Template from '../components/Templates/Template';
import Navigation from '../components/Navigation/Navigation';
import { loadCollection, selectCollection } from '../redux/reducers/collectionSlice';
import { template_load, selectTemplate } from '../redux/reducers/templateSlice';
import NotFound from '../components/NotFound';
import { auth_load } from '../redux/reducers/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import { wrapper } from '../redux/store';
import { navigation_load_geo_list } from '../redux/reducers/navigationSlice';
import { DoesMyCompanySubscribeModal, fetchAssetsForBranding } from '@emarketer/componentlibrary';
import { AdBanner, AD_SLOT_TYPE__BOTTOM, AD_SLOT_TYPE__TOP, IAdSlotConfig } from '@emarketer/googleadmanager';
import { AdBottomWrapper } from '../components/AdWrappers/AdWrappers';
import logger from '../logger/logger';
import { getDataLayerPropsForIndividualForecast } from '../helpers/DataLayerHelper';
import { getSvcDependenciesFromProcessEnv, isShowServiceDebugInfo } from '@emarketer/debuginfo';
import { trimTrailingSlash } from '@emarketer/code-helpers';
import { useUser } from '@emarketer/auth';

const brandingProps = {
  enablePodRedirection: true,
  enableStickyNav: true,
  isTransparent: false,
};

const Forecast = (props: any) => {
  useEffect(() => {
    if (typeof window !== 'undefined' && window.iiAna) {
      window.iiAna('page');
    }
  }, []);

  const dispatch = useDispatch();
  const collection = useSelector(selectCollection);
  const template = useSelector(selectTemplate);

  const [doesMyCompanyModalVisibility, setDoesMyCompanyModalVisibility] = useState<boolean>(false);

  useEffect(() => {
    dispatch(navigation_load_geo_list());
  }, []);

  useEffect(() => {
    if (collection.loaded) {
      dispatch(template_load({ collectionId: collection.id, id: collection.selectedTemplateId }));
    }
  }, [collection]);

  // ERROR PAGE
  if (props.statusCode > 200 || (collection ? collection.id : props.collection?.Id) === null) {
    return <NotFound statusCode={props.statusCode} />;
  }

  /* DMCS Modal needed to be placed in here since the Navigation component's z-index is higher than Collection's component (this is where Chart component resides) */
  const doesMyCompanyModal = () => {
    if (typeof window !== 'undefined' && window.iiAna) {
      return (
        <DoesMyCompanySubscribeModal
          closePopup={() => {
            setDoesMyCompanyModalVisibility(false);
          }}
          visible={doesMyCompanyModalVisibility}
          disablePodRedirection={true}
          returnToCurrentServiceAfterRegistration={false}
          registrationSource={window.location.href}
          doesmyCompanyPluginUrl={config.doesmyCompanyPluginUrl}
        />
      );
    }
    return null;
  };
  const adSlotsConfig: IAdSlotConfig[] = [
    {
      adSlotType: AD_SLOT_TYPE__TOP,
    },
    {
      adSlotType: AD_SLOT_TYPE__BOTTOM,
    },
  ];

  // SEO
  const pageTitle = `${template ? template.titleInTable : props.template?.TitleInTable} ${FORECAST_SLUG_TITLE}`;
  const description = FORECAST_DESCRIPTION(`${template ? template.titleInTable : props.template?.TitleInTable}`);

  const { user } = useUser();

  return (
    <Layout
      title={pageTitle}
      brandingProps={brandingProps}
      description={description}
      url={`${trimTrailingSlash(config.publicPath)}/${collection ? collection.id : props.collection?.Id}`}
      width={'full'}
      canonicalUrl={`${trimTrailingSlash(config.publicPath)}/${collection ? collection.id : props.collection?.Id}/${
        template ? template.id : props.template?.Id
      }`}
      adSlotsConfig={adSlotsConfig}
      brandingAssets={props.brandingAssets}
      topics={template ? template.topics : []}
      publicationDate={template.publicationDate}
      lastModifiedTimestamp={template.lastModifiedTimestamp}
      dataLayerProps={getDataLayerPropsForIndividualForecast({
        collection,
        propsCollectionId: props.collection?.Id,
        template,
        userSubscriptionTier: user.subscriptionTier,
      })}
      serviceDependenciesDebugInfo={props.serviceDependenciesDebugInfo}
    >
      <Navigation
        selectedGeography={template.geography}
        selectedCollectionName={collection ? collection.name : props.collection?.Name}
      />
      <Collection>
        <Template onDoesMyCompanySubscribe={() => setDoesMyCompanyModalVisibility(true)} />
      </Collection>
      <AdBottomWrapper>
        <AdBanner adSlotType={AD_SLOT_TYPE__BOTTOM} />
      </AdBottomWrapper>
      {doesMyCompanyModal()}
    </Layout>
  );
};

export const getServerSideProps = wrapper.getServerSideProps((store) => async (context): Promise<any> => {
  const slug = context.params.slug;
  const collectionId = slug.length > 0 ? slug[0] : '';
  const templateId = slug.length > 1 ? slug[1] : '';
  if (collectionId === 'forecasts' && templateId === 'images') {
    // To prevent server request to public image paths (/forecasts/images/..)
    return;
  }

  //#region DEBUG-INFO
  // Expose service & components version/debug info if non-prod environment. Because we want to show debug info of current service plus its key dependencies (e.g. Component Library), we need to pull "dependencies info" from PROCESS.ENV.XXXX, which is only available SERVER-SIDE. So, we "read" those here and then pass that array of dependencies to render client-side. Read more in "DebugInfo" readme/wiki
  const serviceDependenciesDebugInfo = isShowServiceDebugInfo(config.currentEnvironment, context.req)
    ? getSvcDependenciesFromProcessEnv(process.env)
    : [];
  //#endregion DEBUG-INFO

  const statusCode = context.res.statusCode;

  // Parse auth token server-side so @emarketer/auth/server never runs in the client bundle
  await store.dispatch(auth_load(context.req.headers.cookie ?? ''));
  const collectionReq = await store.dispatch(loadCollection({ collectionId, templateId }));
  if (!collectionReq.payload) {
    return { notFound: true };
  }

  const url = process.env.COMMON_DYNAMIC_CONTENT_WEBAPI!;
  const user = process.env.COMMON_DYNAMIC_CONTENT_USER!;
  const password = process.env.COMMON_DYNAMIC_CONTENT_PASSWORD!;
  const brandingAssets = await fetchAssetsForBranding(url, user, password, fetch, logger);

  const firstTemplateId = collectionReq.payload?.Groups[0]?.Templates[0]?.Id;

  // If no templateId in URL, redirect to the first template to avoid 404
  if (!templateId && firstTemplateId) {
    return {
      redirect: {
        destination: `/${collectionId}/${firstTemplateId}`,
        permanent: false, // 302 temporary redirect
      },
    };
  }

  const templateReq = await store.dispatch(template_load({ collectionId, id: templateId ?? firstTemplateId }));

  if (!templateReq.payload) {
    return {
      notFound: true, // triggers 404
    };
  }

  return {
    props: {
      collectionId,
      templateId,
      statusCode,
      collection: collectionReq?.payload,
      template: templateReq?.payload,
      brandingAssets,
      serviceDependenciesDebugInfo,
    },
  };
});

export default Forecast;
